<template>
  <div class="hezabat-page hezabat-page--table-only">
    <section class="hezabat-filters" aria-label="Policy filters" @keyup.enter="applyFilters">
      <div class="hezabat-filters__grid">
        <div class="hezabat-filters__field hezabat-filters__field--date-from">
          <label class="hezabat-filters__label" for="policy-filter-date-from">{{ $t("policiesFilterDateFrom") }}</label>
          <el-date-picker
            id="policy-filter-date-from"
            v-model="filterDraft.dateFrom"
            type="date"
            class="report-period__date report-period__date--single hezabat-filters__date"
            value-format="YYYY-MM-DD"
            :placeholder="$t('policiesFilterDateFrom')"
            :disabled="loading"
            :clearable="false"
          />
        </div>
        <div class="hezabat-filters__field hezabat-filters__field--date-to">
          <label class="hezabat-filters__label" for="policy-filter-date-to">{{ $t("policiesFilterDateTo") }}</label>
          <el-date-picker
            id="policy-filter-date-to"
            v-model="filterDraft.dateTo"
            type="date"
            class="report-period__date report-period__date--single hezabat-filters__date"
            value-format="YYYY-MM-DD"
            :placeholder="$t('policiesFilterDateTo')"
            :disabled="loading"
            :clearable="false"
          />
        </div>
        <div class="hezabat-filters__field hezabat-filters__field--number">
          <label class="hezabat-filters__label" for="policy-filter-number">{{ $t("policiesFilterPolicyNumber") }}</label>
          <el-input
            id="policy-filter-number"
            v-model="filterDraft.policyNumber"
            clearable
            :disabled="loading"
            :placeholder="$t('policiesFilterPolicyNumberPh')"
          />
        </div>
        <div class="hezabat-filters__field hezabat-filters__field--insured">
          <label class="hezabat-filters__label" for="policy-filter-insured">{{ $t("transactionInsuredClientLabel") }}</label>
          <el-input
            id="policy-filter-insured"
            v-model="filterDraft.insuredClient"
            clearable
            :disabled="loading"
            :placeholder="$t('policiesFilterInsuredPh')"
          />
        </div>
        <div class="hezabat-filters__field hezabat-filters__field--status">
          <label class="hezabat-filters__label" for="policy-filter-status">{{ $t("colStatus") }}</label>
          <el-select
            id="policy-filter-status"
            v-model="filterDraft.status"
            clearable
            :disabled="loading"
            :placeholder="$t('policiesFilterAllStatuses')"
            class="hezabat-filters__select"
            @change="applyFilters"
          >
            <el-option
              v-for="opt in statusFilterOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
        </div>
        <div class="hezabat-filters__field hezabat-filters__field--actions">
          <span class="hezabat-filters__label hezabat-filters__label--spacer" aria-hidden="true">&nbsp;</span>
          <div class="hezabat-filters__actions">
            <button
              type="button"
              class="report-period__search"
              :disabled="loading"
              @click="applyFilters"
            >
              <i class="fa fa-search" aria-hidden="true" />
              <span>{{ $t("search") }}</span>
            </button>
            <button
              v-if="hasActiveFilters"
              type="button"
              class="report-period__reset"
              :disabled="loading"
              :title="$t('policiesFilterClear')"
              @click="clearFilters"
            >
              {{ $t("policiesFilterClear") }}
            </button>
          </div>
        </div>
        <div
          v-if="hasExpiringPolicies"
          class="hezabat-filters__field hezabat-filters__field--expiring"
        >
          <span class="hezabat-filters__label hezabat-filters__label--spacer" aria-hidden="true">&nbsp;</span>
          <button
            type="button"
            class="hezabat-filters__expiring-btn"
            :class="{ 'hezabat-filters__expiring-btn--active': isDaysLeftFilterActive }"
            :disabled="loading"
            :title="$t('policiesExpiringSoonBtnTitle')"
            @click="applyExpiringSoonFilter"
          >
            <span class="hezabat-filters__expiring-btn-icon" aria-hidden="true">⚠</span>
            <span>{{ $t("policiesExpiringSoonBtn") }}</span>
          </button>
        </div>
      </div>
    </section>

    <article
      v-loading="loading"
      class="hezabat-card hezabat-card--fill"
      element-loading-background="rgba(5, 7, 13, 0.55)"
    >
      <el-table
        :data="policies"
        class="hezabat-table"
        stripe
        :height="policyTableHeight"
        :row-class-name="policyRowClassName"
      >
        <el-table-column prop="policyNumber" :label="$t('policy_number')" min-width="180">
          <template #default="{ row }">
            <router-link
              v-if="row.policyId"
              class="policy-detail-link"
              :to="{
                name: 'PolicyDetail',
                params: { id: row.policyId },
                query: policyDetailQuery(row),
                state: { agentListRow: row },
              }"
            >
              {{ row.policyNumber }}
            </router-link>
            <span v-else>{{ row.policyNumber }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="insuredClient" :label="$t('transactionInsuredClientLabel')" min-width="220" />
        <el-table-column prop="productName" :label="$t('transactionProductLabel')" min-width="250" />
        <el-table-column prop="statusName" :label="$t('colStatus')" min-width="260">
          <template #default="{ row }">
            <span class="policy-status" :class="statusClass(row)">
              {{ row.statusName || "—" }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="endDate" :label="$t('endDateLabel')" min-width="160" />
        <el-table-column prop="daysLeft" :label="$t('remainingDays')" min-width="180" sortable>
          <template #default="{ row }">
            <span class="policy-days" :class="{ 'policy-days--warn': isExpiringSoon(row) }">
              <span v-if="isExpiringSoon(row)" class="policy-days__icon">⚠</span>
              {{ formatDaysLeft(row) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </article>

    <!-- Графики: см. PoliciesChartsSection.vue — <PoliciesChartsSection :viewport-height="viewportHeight" /> -->
  </div>
</template>

<script>
import {
  loadAgentRequestsByAgent,
  POLICIES_EXPIRING_SOON_DAYS,
  POLICY_STATUS_ID_BY_KEY,
  POLICY_STATUS_ORDER,
  policyStatusCssClassForRow,
  policyStatusLabelFromId,
} from "./agentRequestsApi.js";
import { defaultPoliciesDateRange, normalizeFilterPeriodDates, toDateInputValue } from "./reportStats.js";

export default {
  name: "HezabatPoliciesPage",
  data() {
    const range = defaultPoliciesDateRange();
    const dateFrom = toDateInputValue(range[0]);
    const dateTo = toDateInputValue(range[1]);
    return {
      loading: false,
      viewportHeight: typeof window !== "undefined" ? window.innerHeight : 900,
      hasExpiringPolicies: false,
      activeFilterMode: null,
      appliedExpiringMaxDays: null,
      policies: [],
      filterDraft: {
        policyNumber: "",
        insuredClient: "",
        status: "",
        dateFrom,
        dateTo,
      },
      appliedFilters: {
        policyNumber: "",
        insuredClient: "",
        status: "",
        dateFrom,
        dateTo,
      },
    };
  },
  computed: {
    statusFilterOptions() {
      return POLICY_STATUS_ORDER.map((statusKey) => ({
        value: statusKey,
        label: policyStatusLabelFromId(POLICY_STATUS_ID_BY_KEY[statusKey]) || statusKey,
      }));
    },
    defaultPeriodBounds() {
      const range = defaultPoliciesDateRange();
      return {
        dateFrom: toDateInputValue(range[0]),
        dateTo: toDateInputValue(range[1]),
      };
    },
    isDaysLeftFilterActive() {
      return this.activeFilterMode === "daysLeft";
    },
    hasActiveFilters() {
      const f = this.appliedFilters;
      const def = this.defaultPeriodBounds;
      return Boolean(
        this.isDaysLeftFilterActive ||
          String(f.policyNumber || "").trim() ||
          String(f.insuredClient || "").trim() ||
          f.status ||
          f.dateFrom !== def.dateFrom ||
          f.dateTo !== def.dateTo
      );
    },
    policyTableHeight() {
      const w = typeof window !== "undefined" ? window.innerWidth : 1200;
      const filterBand = 72;
      let base;
      if (w >= 768 && w < 1100) {
        base = Math.max(280, Math.min(440, Math.floor(this.viewportHeight * 0.5)));
      } else {
        base = Math.max(220, Math.min(520, Math.floor(this.viewportHeight * 0.62)));
      }
      return Math.max(200, base - filterBand);
    },
  },
  mounted() {
    this.loadPolicies();
    window.addEventListener("resize", this.onViewportResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onViewportResize);
  },
  methods: {
    onViewportResize() {
      this.viewportHeight = window.innerHeight;
    },
    defaultDateState() {
      const range = defaultPoliciesDateRange();
      const dateFrom = toDateInputValue(range[0]);
      const dateTo = toDateInputValue(range[1]);
      return { dateFrom, dateTo };
    },
    applyFilters() {
      const { dateFrom, dateTo } = normalizeFilterPeriodDates(
        this.filterDraft.dateFrom,
        this.filterDraft.dateTo,
        defaultPoliciesDateRange
      );
      this.filterDraft.dateFrom = dateFrom;
      this.filterDraft.dateTo = dateTo;
      this.activeFilterMode = "standard";
      this.appliedExpiringMaxDays = null;
      this.appliedFilters = {
        policyNumber: String(this.filterDraft.policyNumber || "").trim(),
        insuredClient: String(this.filterDraft.insuredClient || "").trim(),
        status: this.filterDraft.status || "",
        dateFrom,
        dateTo,
      };
      this.loadPolicies();
    },
    applyExpiringSoonFilter() {
      this.activeFilterMode = "daysLeft";
      this.appliedExpiringMaxDays = POLICIES_EXPIRING_SOON_DAYS;
      this.loadPolicies();
    },
    clearFilters() {
      const dates = this.defaultDateState();
      this.filterDraft = {
        policyNumber: "",
        insuredClient: "",
        status: "",
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
      };
      this.activeFilterMode = null;
      this.appliedExpiringMaxDays = null;
      this.appliedFilters = {
        policyNumber: "",
        insuredClient: "",
        status: "",
        dateFrom: dates.dateFrom,
        dateTo: dates.dateTo,
      };
      this.loadPolicies();
    },
    async loadPolicies() {
      this.loading = true;
      try {
        const apiFilters = this.isDaysLeftFilterActive
          ? {
              searchByDaysLeft: true,
              searchByStandard: false,
              maxDaysLeft: this.appliedExpiringMaxDays,
            }
          : {
              searchByDaysLeft: false,
              searchByStandard: true,
              ...this.appliedFilters,
            };
        const { policies, hasAny } = await loadAgentRequestsByAgent(apiFilters);
        this.policies = policies;
        if (hasAny === true) {
          this.hasExpiringPolicies = true;
        } else if (hasAny === false && !this.isDaysLeftFilterActive) {
          this.hasExpiringPolicies = false;
        }
      } catch (err) {
        this.policies = [];
        if (err?.response?.status === 401) {
          this.$router.push("/signIn/catalog");
        }
      } finally {
        this.loading = false;
      }
    },
    statusClass(row) {
      return policyStatusCssClassForRow(row);
    },
    formatDaysLeft(row) {
      if (row?.daysLeft != null && row.daysLeft !== "") {
        return Math.max(0, Math.floor(Number(row.daysLeft)));
      }
      return "—";
    },
    isExpiringSoon(row) {
      const threshold = this.isDaysLeftFilterActive ? this.appliedExpiringMaxDays : null;
      if (threshold == null) return false;
      const left = row?.daysLeft;
      if (left == null || left === "") return false;
      const n = Number(left);
      return Number.isFinite(n) && n >= 0 && n <= threshold;
    },
    policyRowClassName({ row }) {
      return this.isExpiringSoon(row) ? "policy-row--expiring" : "";
    },
    policyDetailQuery(row) {
      const q = {};
      if (row?.isPolicyKind) {
        q.type = row.isPolicyKind;
      } else if (row?.isPolicyRaw) {
        q.type = row.isPolicyRaw;
      }
      if (row?.isPolicyKind === "PROD" && row?.reqId) {
        q.reqId = String(row.reqId);
      }
      return Object.keys(q).length ? q : undefined;
    },
  },
};
</script>

<style src="@/assets/scss/hezabat.scss" lang="scss"></style>
<style src="@/assets/scss/report-page.scss" lang="scss"></style>
